import React, { useState } from "react";
import { Camera, Upload, Check, X, Loader } from "lucide-react";
import { Button } from "../shared/Button";
import { fotos as apiFotos } from "../../api/modulos.service";
import { estilos as est, Seleccion } from "./base";
import CamaraFoto from "./CamaraFoto";

/**
 * TOMAR O ADJUNTAR FOTOGRAFÍAS.
 *
 * Dos caminos a propósito: **tomar** con la cámara —que es lo que se usa en la
 * caseta— y **adjuntar** desde el aparato, que es lo que sirve desde una
 * computadora. El botón de cámara abre la cámara de verdad y no el selector de
 * archivos: `<input capture>` lo ignora el escritorio, y ése fue un fallo que
 * ya hubo que corregir una vez en otro sistema de la casa.
 *
 * Cada foto se etiqueta con QUÉ es —visitante, DUI, placa—. Sin eso, a los
 * tres meses el historial tiene cuatro imágenes sin saber cuál es cuál.
 */
export default function CapturaFotos({ ambito, recursoId, alGuardar, tipos }) {
  const [pendientes, setPendientes] = useState([]);
  const [tipo, setTipo] = useState("visitante");
  const [subiendo, setSubiendo] = useState(false);
  const [camaraAbierta, setCamaraAbierta] = useState(false);
  const [error, setError] = useState("");

  const TIPOS = tipos || [
    { valor: "visitante", etiqueta: "Visitante" },
    { valor: "dui_frente", etiqueta: "DUI (frente)" },
    { valor: "dui_reverso", etiqueta: "DUI (reverso)" },
    { valor: "licencia", etiqueta: "Licencia" },
    { valor: "vehiculo", etiqueta: "Vehículo" },
    { valor: "placa", etiqueta: "Placa" },
    { valor: "otro", etiqueta: "Otro" },
  ];

  const subir = async (archivo) => {
    setError("");
    setSubiendo(true);
    try {
      const confirmada = await apiFotos.subir(archivo, { ambito, recursoId });
      const nueva = { ...confirmada, kind: tipo, vistaPrevia: URL.createObjectURL(archivo) };
      const lista = [...pendientes, nueva];
      setPendientes(lista);
      if (alGuardar) {
        await alGuardar(lista.map(({ key, kind, contentType, size }) => ({ key, kind, contentType, size })));
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "No se pudo subir la imagen");
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: "var(--space-3)", width: "100%" }}>
      <Seleccion
        etiqueta="Qué va a fotografiar"
        value={tipo}
        onChange={(e) => setTipo(e.target.value)}
        opciones={TIPOS}
      />

      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-3)" }}>
        <Button
          type="button"
          variant="secondary"
          leftIcon={Camera}
          onClick={() => setCamaraAbierta(true)}
          disabled={subiendo}
        >
          Tomar foto
        </Button>

        <label>
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => {
              const archivo = e.target.files?.[0];
              if (archivo) subir(archivo);
              e.target.value = "";
            }}
          />
          <Button as="span" type="button" variant="outline" leftIcon={Upload} disabled={subiendo}>
            Elegir archivo
          </Button>
        </label>

        {subiendo && (
          <span style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", color: "var(--text-secondary)" }}>
            <Loader size={16} /> Subiendo…
          </span>
        )}
      </div>

      {error && <p style={{ color: "var(--danger)", fontSize: "var(--text-sm)", margin: 0 }}>{error}</p>}

      {pendientes.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-3)" }}>
          {pendientes.map((f) => (
            <div key={f.key} style={{ position: "relative" }}>
              <img
                src={f.vistaPrevia}
                alt={f.kind}
                style={{
                  width: "88px",
                  height: "88px",
                  objectFit: "cover",
                  borderRadius: "var(--radius-md)",
                  border: "2px solid var(--success)",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: "2px 4px",
                  fontSize: "10px",
                  textAlign: "center",
                  backgroundColor: "rgba(0,0,0,0.65)",
                  color: "#fff",
                  borderBottomLeftRadius: "var(--radius-md)",
                  borderBottomRightRadius: "var(--radius-md)",
                }}
              >
                {TIPOS.find((t) => t.valor === f.kind)?.etiqueta || f.kind}
              </span>
            </div>
          ))}
        </div>
      )}

      {camaraAbierta && (
        <CamaraFoto
          alCerrar={() => setCamaraAbierta(false)}
          alCapturar={(archivo) => {
            setCamaraAbierta(false);
            subir(archivo);
          }}
        />
      )}
    </div>
  );
}
